import { Color } from "@bonliva-crm/ui";
import { Box, Popover, IconButton, InputLabel, makeStyles } from "@material-ui/core";
import React, { useRef, useState } from "react";
import { ChevronDownIcon, CloseIcon } from "../../../icons";
import { SelectBadge } from "./components/select-badge";

type Props = {
  value?: React.ReactNode | string;
  content?: React.ReactNode;
  placeholder?: React.ReactNode;
  label?: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  openIcon?: React.ReactNode;
  openByDefault?: boolean;
  showPlaceholderOnExpand?: boolean;
  hideBorder?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  badgeLabel?: string | number;
  maxMenuHeight?: number;
};

export function SelectMenu(props: Props) {
  const [_open, _setOpen] = useState(props.openByDefault ?? false);
  const anchorElRef = useRef(null);

  const open = props.open ?? _open;
  const setOpen = props.setOpen ?? _setOpen;

  const { root, valueContainer, valueWrapper, contentWrapper, chevron } = useStyles({
    open,
    hideBorder: props.hideBorder,
    maxMenuHeight: props.maxMenuHeight
  });
  const handleClose = () => {
    setOpen(!open);
  };

  const id = open ? "simple-popover" : undefined;

  return (
    <div ref={anchorElRef} className={root}>
      {props.label && <InputLabel>{props.label}</InputLabel>}
      <Box className={valueContainer}>
        {props.badgeLabel && (
          <SelectBadge
            selectedAmount={typeof props.badgeLabel === "number" ? props.badgeLabel : 1}
            text={typeof props.badgeLabel === "string" ? props.badgeLabel : ""}
          />
        )}
        <Box
          className={valueWrapper}
          onClick={(e) => {
            setOpen(!open);
            e.preventDefault();
          }}
        >
          <Box
            style={{ overflowX: "hidden", userSelect: "none", scrollbarWidth: "none" }}
          >
            {props.value ||
              ((!open || props.showPlaceholderOnExpand) && props.placeholder)}
          </Box>
          <span style={{ display: "flex", gap: "0.5rem" }}>
            {props.isClearable && props.onClear ? (
              <IconButton
                style={{ padding: 0, width: "1rem", height: "1rem" }}
                onClick={(e) => {
                  props.onClear?.();
                  e.stopPropagation();
                }}
              >
                <CloseIcon
                  htmlColor={Color.Body}
                  style={{ width: "14px", height: "14px" }}
                />
              </IconButton>
            ) : undefined}
            {props.openIcon ?? <ChevronDownIcon className={chevron} />}
          </span>
        </Box>
        <Popover
          transitionDuration={0}
          id={id}
          open={open}
          anchorEl={anchorElRef.current}
          onClose={handleClose}
          PaperProps={{
            style: {
              overflow: "visible" // This is needed to make the popover content visible outside the popover ex: Datepicker
            }
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left"
          }}
        >
          <Box className={contentWrapper}>{props.content}</Box>
        </Popover>
      </Box>
    </div>
  );
}

type StyleProps = {
  open: boolean;
  position?: "top" | "bottom";
  hideBorder?: boolean;
  maxMenuHeight?: number;
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
    position: "relative"
  },
  valueContainer: (props: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    border: props.hideBorder ? undefined : `1px solid ${Color.LightGray}`,
    borderRadius: "0.25rem",
    minHeight: "2.5rem",
    transition: "0.2s ease-in",
    scrollbarWidth: "none",
    overflowY: "hidden"
  }),
  valueWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "1rem",
    alignItems: "center",
    scrollbarWidth: "none",
    overflowY: "hidden",
    // height: "calc(2.5rem - 2px)",
    // maxHeight: "calc(2.5rem - 2px)",
    minHeight: "calc(2.5rem - 2px)",
    padding: "0.5rem 0.75rem",
    "&:hover": {
      cursor: "pointer"
    }
  },
  contentWrapper: (props: StyleProps) => ({
    padding: "1rem 0.75rem",
    maxHeight: props.maxMenuHeight ? props.maxMenuHeight : "auto"
  }),
  chevron: (props: StyleProps) => ({
    width: "1rem",
    height: "1rem",
    transform: props.open ? "rotate(-180deg)" : "rotate(0deg)",
    transition: "0.2s ease",
    marginRight: "0.1rem"
  })
}));

export default SelectMenu;
